import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Paper,
  Avatar
} from '@mui/material';
import {
  History as HistoryIcon,
  Restore as RestoreIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { changelogAPI } from '../services/apiWithAuth';
import useAppStore from '../store/useAppStore';
import { useMsal } from '@azure/msal-react';

const ChangeLogDialog = ({ 
  open, 
  onClose, 
  employeeCode, 
  year, 
  month, 
  day,
  currentValue,
  onRestore
}) => {
  const { employees, workTypes } = useAppStore();
  const { accounts } = useMsal();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(null);

  // Fetch changelog when dialog opens
  useEffect(() => {
    if (open && employeeCode && year && month && day) {
      fetchCellChangelog();
    }
  }, [open, employeeCode, year, month, day]);

  const fetchCellChangelog = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await changelogAPI.getCellChangelog(employeeCode, year, month, day);
      
      // Group changes by timestamp, user and operation (within 2 seconds)
      const grouped = groupChanges(response.changes || []);
      setChanges(grouped);
    } catch (err) {
      console.error('Error fetching changelog:', err);
      setError('Nie udało się pobrać historii zmian');
    } finally {
      setLoading(false);
    }
  };

  // Group changes that happened at the same time by the same user
  const groupChanges = (changes) => {
    if (!changes || changes.length === 0) return [];
    
    const groups = [];
    let currentGroup = null;
    
    changes.forEach(change => {
      const changeTime = new Date(change.changed_at).getTime();
      
      // Skip empty changes
      if (!change.old_value && !change.new_value) return;
      
      // Check if should join existing group (same user, within 2 seconds)
      const shouldGroup = currentGroup && 
        currentGroup.changed_by === change.changed_by &&
        Math.abs(changeTime - new Date(currentGroup.changed_at).getTime()) < 2000;
      
      if (!shouldGroup) {
        // Start new group
        currentGroup = {
          ...change,
          // Determine primary operation type: create > delete > update
          operation_type: change.operation_type,
          fields: [{ 
            field_name: change.field_name, 
            old_value: change.old_value, 
            new_value: change.new_value,
            id: change.id,
            operation_type: change.operation_type
          }]
        };
        groups.push(currentGroup);
      } else {
        // Add to existing group
        currentGroup.fields.push({ 
          field_name: change.field_name, 
          old_value: change.old_value, 
          new_value: change.new_value,
          id: change.id,
          operation_type: change.operation_type
        });
        
        // Update primary operation type if needed (create > delete > update)
        if (change.operation_type === 'create' && currentGroup.operation_type !== 'create') {
          currentGroup.operation_type = 'create';
        } else if (change.operation_type === 'delete' && currentGroup.operation_type === 'update') {
          currentGroup.operation_type = 'delete';
        }
      }
    });
    
    return groups;
  };

  const handleRestore = async (changeLogId, oldValue, fieldName) => {
    setRestoring(changeLogId);
    
    try {
      await changelogAPI.restore(changeLogId);
      
      // Call parent callback if provided
      if (onRestore) {
        onRestore({
          changeLogId,
          oldValue,
          fieldName,
          employeeCode,
          year,
          month,
          day
        });
      }
      
      // Refresh changelog
      await fetchCellChangelog();
      
    } catch (err) {
      console.error('Error restoring value:', err);
      setError('Nie udało się przywrócić poprzedniej wartości');
    } finally {
      setRestoring(null);
    }
  };

  const getOperationIcon = (operationType) => {
    switch (operationType) {
      case 'create':
        return <AddIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />;
      case 'update':
        return <EditIcon sx={{ color: '#2196f3', fontSize: '1.2rem' }} />;
      case 'delete':
        return <DeleteIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />;
      default:
        return <HistoryIcon sx={{ color: '#757575', fontSize: '1.2rem' }} />;
    }
  };

  const getOperationColor = (operationType) => {
    switch (operationType) {
      case 'create':
        return 'success';
      case 'update':
        return 'primary';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  const getOperationLabel = (operationType) => {
    switch (operationType) {
      case 'create':
        return 'Utworzono';
      case 'update':
        return 'Zmieniono';
      case 'delete':
        return 'Usunięto';
      default:
        return operationType;
    }
  };

  const getOperationBackground = (operationType) => {
    switch (operationType) {
      case 'create':
        return 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)';
      case 'update':
        return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
      case 'delete':
        return 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
      default:
        return 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)';
    }
  };

  const getFieldLabel = (fieldName) => {
    switch (fieldName) {
      case 'work_type_code':
        return 'Typ pracy';
      case 'custom_text':
        return 'Tekst niestandardowy';
      case 'updated_by':
        return 'Zmienione przez';
      case 'employee_code':
        return 'Kod pracownika';
      default:
        return fieldName;
    }
  };

  const formatDateTime = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      return format(date, 'dd.MM.yyyy HH:mm:ss', { locale: pl });
    } catch (err) {
      return dateTimeString;
    }
  };

  // Get employee name from code
  const getEmployeeName = (changedBy) => {
    if (!changedBy || changedBy === 'user') {
      return 'Użytkownik';
    }
    
    if (changedBy === 'system') {
      return 'System';
    }
    
    // First try to find employee by email (changedBy should be email from backend)
    const employee = employees.find(emp => 
      emp.email && emp.email.toLowerCase() === changedBy.toLowerCase()
    );
    
    if (employee) {
      return `${employee.name} (${employee.code})`;
    }
    
    // Try to find by code
    const empByCode = employees.find(emp => emp.code === changedBy);
    if (empByCode) {
      return `${empByCode.name} (${empByCode.code})`;
    }
    
    // If not found in employees, try to match with MSAL accounts
    if (accounts && accounts.length > 0) {
      const account = accounts.find(acc => 
        acc.username && acc.username.toLowerCase() === changedBy.toLowerCase()
      );
      if (account) {
        return account.name || 'Użytkownik';
      }
    }
    
    // Last resort - return generic name, NOT email
    return 'Użytkownik';
  };

  // Render visual work type chip
  const renderWorkTypeChip = (workTypeCode) => {
    if (!workTypeCode || workTypeCode === '(puste)' || workTypeCode === 'BRAK') {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '60px',
            height: '50px',
            borderRadius: '8px',
            backgroundColor: '#f5f5f5',
            border: '2px solid #e0e0e0',
            fontSize: '1.1rem',
            fontWeight: 700,
            color: '#9e9e9e',
          }}
        >
          BRAK
        </Box>
      );
    }

    const workType = workTypes.find(wt => wt.code === workTypeCode);
    const backgroundColor = workType?.color || '#cccccc';

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '60px',
          height: '50px',
          borderRadius: '8px',
          backgroundColor: backgroundColor,
          color: '#ffffff',
          fontSize: '1.2rem',
          fontWeight: 700,
          border: '2px solid rgba(0,0,0,0.2)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }}
        title={workType?.name || workTypeCode}
      >
        {workTypeCode}
      </Box>
    );
  };

  const formatValue = (value, fieldName) => {
    // Sprawdź różne przypadki pustych wartości
    if (value === null || 
        value === undefined || 
        value === '' || 
        value === 'undefined' || 
        value === 'None' || 
        value === 'null') {
      return fieldName === 'work_type_code' ? renderWorkTypeChip('') : <em style={{ color: '#999' }}>brak</em>;
    }
    
    if (value === 'Del') {
      return fieldName === 'work_type_code' ? renderWorkTypeChip('Del') : <em style={{ color: '#ff6b6b' }}>usunięto</em>;
    }
    
    if (fieldName === 'work_type_code') {
      return renderWorkTypeChip(value);
    }
    
    if (fieldName === 'custom_text') {
      return <em style={{ color: '#7b1fa2' }}>"{value}"</em>;
    }
    
    if (fieldName === 'updated_by') {
      return <span style={{ fontWeight: '500' }}>{getEmployeeName(value)}</span>;
    }
    
    return <span style={{ fontWeight: '500' }}>{value}</span>;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          <Typography variant="h6">
            Historia zmian - {employeeCode}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {day}.{month.toString().padStart(2, '0')}.{year}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Current value card with better styling */}
        {currentValue && (
          <Card sx={{ 
            mb: 3,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            border: '1px solid #1565c0'
          }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <HistoryIcon sx={{ fontSize: '20px' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Aktualna wartość
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {currentValue.work_type_code && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                      Typ pracy:
                    </Typography>
                    {renderWorkTypeChip(currentValue.work_type_code)}
                  </Box>
                )}
                {currentValue.custom_text && (
                  <Chip 
                    label={`Tekst: ${currentValue.custom_text}`}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontWeight: 500
                    }}
                    size="small"
                  />
                )}
                {currentValue.updated_by && (
                  <Chip 
                    icon={<PersonIcon sx={{ fontSize: '16px !important', color: 'white !important' }} />}
                    label={`Ostatnio: ${getEmployeeName(currentValue.updated_by)}`}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontWeight: 500
                    }}
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading spinner */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Changes list */}
            {changes.length === 0 ? (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: 2,
                  border: '1px solid #e3f2fd',
                  backgroundColor: '#f3e5f5'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Brak historii zmian dla tej komórki
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Ta komórka nie została jeszcze edytowana lub nie ma zapisanych zmian.
                </Typography>
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #bdbdbd' }}>
                  <Typography variant="h6" sx={{ 
                    color: 'text.primary',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <HistoryIcon />
                    Historia zmian ({changes.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Chronologiczna lista wszystkich modyfikacji tej komórki
                  </Typography>
                </Box>
              <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                {changes.map((change, index) => (
                  <React.Fragment key={change.id}>
                    <Paper 
                      elevation={1}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      {/* Header with operation type and user */}
                      <Box 
                        sx={{
                          background: change.operation_type === 'create' 
                            ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                            : change.operation_type === 'update'
                            ? 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)'
                            : 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                          color: 'white',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          {getOperationIcon(change.operation_type)}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                              {getOperationLabel(change.operation_type)}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                              <PersonIcon sx={{ fontSize: '14px', opacity: 0.9 }} />
                              <Typography variant="caption" sx={{ opacity: 0.95, fontSize: '0.75rem' }}>
                                {getEmployeeName(change.changed_by)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <TimeIcon sx={{ fontSize: '16px', opacity: 0.9 }} />
                          <Typography variant="caption" sx={{ opacity: 0.95, fontSize: '0.75rem' }}>
                            {formatDateTime(change.changed_at)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Content with field changes */}
                      <Box p={2}>
                        {change.fields.map((field, fieldIndex) => (
                          <Box key={fieldIndex} mb={fieldIndex < change.fields.length - 1 ? 2 : 0}>
                            <Typography variant="caption" sx={{ 
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              letterSpacing: '0.5px'
                            }}>
                              {getFieldLabel(field.field_name)}
                            </Typography>

                            <Box display="flex" alignItems="center" gap={2} mt={1.5} flexWrap="wrap">
                              {field.old_value !== null && (
                                <Box flex={1} minWidth="120px">
                                  <Typography variant="caption" sx={{ 
                                    color: '#d32f2f',
                                    fontWeight: 'bold',
                                    fontSize: '0.65rem',
                                    display: 'block',
                                    mb: 0.5
                                  }}>
                                    PRZED
                                  </Typography>
                                  <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                      p: 1.5,
                                      backgroundColor: '#ffebee',
                                      borderColor: '#ffcdd2',
                                      borderRadius: 1.5
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: 500,
                                      color: '#c62828',
                                      wordBreak: 'break-word'
                                    }}>
                                      {formatValue(field.old_value, field.field_name)}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                              
                              {field.old_value !== null && field.new_value !== null && (
                                <Box sx={{ 
                                  fontSize: '1.5rem', 
                                  color: '#9e9e9e',
                                  alignSelf: 'center',
                                  mt: 2
                                }}>
                                  →
                                </Box>
                              )}
                              
                              {field.new_value !== null && (
                                <Box flex={1} minWidth="120px">
                                  <Typography variant="caption" sx={{ 
                                    color: '#388e3c',
                                    fontWeight: 'bold',
                                    fontSize: '0.65rem',
                                    display: 'block',
                                    mb: 0.5
                                  }}>
                                    PO
                                  </Typography>
                                  <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                      p: 1.5,
                                      backgroundColor: '#e8f5e9',
                                      borderColor: '#c8e6c9',
                                      borderRadius: 1.5
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: 500,
                                      color: '#2e7d32',
                                      wordBreak: 'break-word'
                                    }}>
                                      {formatValue(field.new_value, field.field_name)}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}

                        {/* Restore button - only for updates */}
                        {change.operation_type === 'update' && change.fields[0].old_value !== null && (
                          <Box mt={2} display="flex" justifyContent="flex-end">
                            <Tooltip title="Przywróć poprzednią wartość">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RestoreIcon />}
                                onClick={() => handleRestore(change.fields[0].id, change.fields[0].old_value, change.fields[0].field_name)}
                                disabled={restoring === change.fields[0].id}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {restoring === change.fields[0].id ? 'Przywracanie...' : 'Przywróć'}
                              </Button>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </React.Fragment>
                ))}
              </List>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeLogDialog;